import React, { useEffect, useState } from "react";
import { tr } from "date-fns/locale";
import { CalendarDays } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

const toTr = (iso) => (iso && /^\d{4}-\d{2}-\d{2}/.test(iso) ? iso.slice(0, 10).split("-").reverse().join(".") : "");
const toIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const parseIso = (iso) => (toTr(iso) ? new Date(`${iso.slice(0, 10)}T00:00:00`) : undefined);

const parseTyped = (txt) => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(txt)) return txt;
  const m = txt.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
  if (!m) return null;
  const d = new Date(+m[3], +m[2] - 1, +m[1]);
  return d.getDate() === +m[1] && d.getMonth() === +m[2] - 1 ? toIso(d) : null;
};

const mask = (raw) => {
  const d = raw.replace(/\D/g, "").slice(0, 8);
  return [d.slice(0, 2), d.slice(2, 4), d.slice(4)].filter(Boolean).join(".");
};

const CAL_CLASSES = { caption_label: "hidden", caption_dropdowns: "flex gap-1", vhidden: "hidden",
  dropdown: "text-sm border border-slate-300 rounded px-1 py-0.5 bg-white" };

export function DateInput({ value, onChange, className = "", min, max, disabled, placeholder = "gg.aa.yyyy", title, onClick, ...rest }) {
  const [text, setText] = useState(toTr(value));
  const [open, setOpen] = useState(false);
  useEffect(() => setText(toTr(value)), [value]);
  const emit = (iso) => onChange && onChange({ target: { value: iso } });

  const onType = (e) => {
    const raw = e.target.value;
    const next = /^\d{4}-/.test(raw) ? raw : mask(raw);
    setText(next);
    if (!next) return emit("");
    const iso = parseTyped(next);
    if (iso && (!min || iso >= min) && (!max || iso <= max)) emit(iso);
  };
  const pick = (d) => { if (d) { emit(toIso(d)); setOpen(false); } };
  const block = [min && { before: parseIso(min) }, max && { after: parseIso(max) }].filter(Boolean);
  const y = new Date().getFullYear();

  return (
    <span className={`relative ${/\bw-full\b/.test(className) ? "block w-full" : "inline-block"}`} onClick={onClick}>
      <input type="text" inputMode="numeric" value={text} onChange={onType} onBlur={() => setText(toTr(value))}
        placeholder={placeholder} disabled={disabled} title={title} className={`${className} pr-7`} {...rest} />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" disabled={disabled} tabIndex={-1} aria-label="Takvimi aç"
            className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 disabled:opacity-40"
            data-testid={rest["data-testid"] ? `${rest["data-testid"]}-calendar` : undefined}>
            <CalendarDays size={14} />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 z-[100]" align="start" onClick={(e) => e.stopPropagation()}>
          <Calendar mode="single" locale={tr} weekStartsOn={1} selected={parseIso(value)} defaultMonth={parseIso(value)}
            onSelect={pick} disabled={block} captionLayout="dropdown-buttons" fromYear={y - 80} toYear={y + 5}
            classNames={CAL_CLASSES} initialFocus />
        </PopoverContent>
      </Popover>
    </span>
  );
}
